<template>
  <div
    v-if="eSignLoading"
    class="d-flex flex-column justify-content-center align-items-center"
    style="height: 100vh"
  >
    <div class="drip-h3 color-black font-weight-400 mb-3">
      {{ $t('clickWrap.esignPostLoading') }}
    </div>
    <img
      src="@/assets/images/common/page-loader.svg"
      alt="Error Icon"
      class="page-loader"
      width="80"
      height="80"
    />
  </div>
  <div v-else class="click-wrap-view">
    <div class="click-wrap-header">
      <div class="drip-h3 color-white">
        {{ $t('clickWrap.heading1') }} {{ getTodayDate() }}
      </div>
      <div class="drip-body color-white">
        {{ $t('clickWrap.heading2') }}
      </div>
    </div>
    <div id="pageContainer">
      <pdf-viewer
        :docId="docId"
        :completedRead="completedDocRead"
        @completedReadProgress="completedReadProgress"
      />
    </div>
    <div class="click-wrap-footer signature" v-if="completedDocRead">
      <div class="drip-h4 color-noble-blue">
        {{ $t('clickWrap.consentTitle') }}
      </div>
      <div class="d-flex mt-2">
        <div class="checkbox">
          <b-form-checkbox
            key="1"
            v-model="clickWrapCheckbox"
            class="checkbox-group"
            value="accepted"
            unchecked-value="not_accepted"
          >
            {{ $t('clickWrap.consentText') }}
          </b-form-checkbox>
        </div>
      </div>
      <div class="d-flex flex-column mt-2 w-100">
        <div class="click-wrap-label">{{ $t('clickWrap.inputLabel') }}</div>
        <form
          @submit.prevent="esignDocument"
          class="click-wrap-input-container"
          novalidate
        >
          <div class="d-flex flex-column click-wrap-input-inner-container">
            <b-form-group class="mb-0">
              <b-form-input
                name="clickwrap"
                class="click-wrap-input"
                :class="clickWrapCheckbox === 'accepted' ? '' : 'disabled'"
                type="text"
                placeholder="Type your name here"
                v-validate="'required|alpha_num'"
                v-model="signature"
                :disabled="clickWrapCheckbox === 'not_accepted'"
                required
              ></b-form-input>
              <span class="error">{{ errors.first('clickwrap') }}</span>
            </b-form-group>
          </div>
          <button
            type="submit"
            class="rf-button medium solid"
            :class="clickWrapCheckbox === 'accepted' ? '' : 'disabled'"
          >
            {{ $t('clickWrap.inputButton') }}
            <b-icon-chevron-right class="ml-2" />
          </button>
        </form>
      </div>
    </div>
    <div class="click-wrap-footer" v-else>
      <action-button
        variant="text"
        size="small"
        extraClass="scroll-down-button"
        :click="scrollToBottom"
        tabindex="-1"
      >
        {{ $t('clickWrap.scrollToBottom') }}
        <b-icon-chevron-down class="color-wealthy-green-500 ml-2" />
      </action-button>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import PdfViewer from '@/components/clickwrap/PdfViewer'
import ActionButton from '@/components/common/ActionButton'
import { required } from 'vee-validate/dist/rules.esm'
import pdfService from '@/services/clickWrap'

export default {
  components: { PdfViewer, ActionButton },
  data() {
    return {
      completedDocRead: false,
      clickWrapCheckbox: 'not_accepted',
      signature: '',
      eSignLoading: false,
      docId: ''
    }
  },
  created() {
    this.$validator.extend('required', {
      ...required,
      getMessage: () => this.$t('clickWrap.validations.required')
    })
    this.$validator.extend('alpha_num', {
      validate(value) {
        return /^[a-zA-Z0-9\s]*$/.test(value)
      },
      getMessage: () => this.$t('clickWrap.validations.alpha')
    })
    if (this.$route.query.docId) {
      this.docId = this.$route.query.docId
    }
  },
  mounted() {
    this.$mpTrack(`ClickWrap - page loaded`)
  },
  methods: {
    completedReadProgress() {
      this.completedDocRead = true
    },
    scrollToBottom() {
      this.$mpTrack('ClickWrap - clicked on "Scroll to the bottom"')
      const pdfViewer = document.getElementById('pdf-viewer')
      if (pdfViewer) {
        pdfViewer.scrollTop = pdfViewer.scrollHeight
      }
    },
    esignDocument() {
      if (this.clickWrapCheckbox === 'accepted') {
        this.$validator
          .validate()
          .then((result) => {
            if (result) {
              this.eSignLoading = true
              pdfService
                .postClickWrap(this.docId, this.signature)
                .then((res) => {
                  if (res.data.message === 'success') {
                    this.$mpTrack('ClickWrap - Document e-signed successfully')
                    this.$router.push({
                      name: 'dashboard',
                      query: {
                        esign: 'true'
                      }
                    })
                  } else {
                    this.$mpTrack('ClickWrap - Document e-sign failed', {
                      message: res.data && res.data.message
                    })
                    this.eSignLoading = false
                    this.$notify({
                      title: this.$t('clickWrap.esignFailedAlert.title'),
                      text: this.$t('clickWrap.esignFailedAlert.text'),
                      type: 'error'
                    })
                  }
                })
                .catch((e) => {
                  if (e && e.message) {
                    this.$mpTrack('ClickWrap - Document e-sign failed', {
                      message: e.message
                    })
                    this.$notify({
                      title: this.$t('clickWrap.esignNotFoundAlert.title'),
                      text: this.$t('clickWrap.esignNotFoundAlert.text'),
                      type: 'info'
                    })
                  } else {
                    this.$mpTrack('ClickWrap - Document e-sign failed')
                  }
                  this.eSignLoading = false
                })
            }
          })
          .catch((e) => console.log(e))
      }
    },
    getTodayDate() {
      const dateString = new Date().toLocaleDateString()
      return dateString.split('/').join('-')
    }
  }
}
</script>

<style lang="scss" scoped>
.click-wrap-view {
  display: flex;
  flex-direction: column;
  height: 100vh;

  .click-wrap-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;
    justify-content: center;
    background: $noble-blue-500;
    padding: 16px 12px;
    position: relative;
    width: 100%;
  }
  #pageContainer {
    flex-grow: 4;
    margin: auto;
    overflow: hidden;
    background: $background-light;
    height: 100%;
    width: 100%;
  }
  .click-wrap-footer {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $white;
    border-top: 2px solid $sky-blue-500;
    div {
      max-width: 1240px;
    }
    &.signature {
      flex-grow: 2;
      flex-direction: column;
      align-items: flex-start;
      padding: 24px 80px;
      @media screen and (max-width: 1025px) {
        padding: 16px 40px;
      }
      @media screen and (max-width: 576px) {
        padding: 16px 32px;
      }
    }
  }
}
.checkbox-group {
  cursor: pointer;
  padding-left: 24px;
  ::v-deep .custom-control-label {
    cursor: pointer;
    font-family: 'Nunito Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    color: $black;
  }
}
.click-wrap-label {
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  width: 100%;
  margin-bottom: 8px;
}
.click-wrap-input-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;

  @media screen and (max-width: 576px) {
    flex-direction: column;
  }

  .click-wrap-input-inner-container {
    width: auto;
    @media screen and (max-width: 576px) {
      width: 100%;
    }
  }

  .click-wrap-input {
    font-family: 'Nunito Sans';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    border-radius: 8px;
    height: 56px;
    width: 400px;
    @media screen and (max-width: 576px) {
      width: 100%;
      margin-bottom: 12px;
    }
    margin-right: 32px;
    border: 1px solid $noble-blue-300;
    background: $white;
    color: $black;
    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(33, 125, 229, 0.25);
    }
    &.disabled {
      border: 1px solid $disabled-grey;
      background: $grey;
    }
  }
  .error {
    color: $red-400;
    font-family: 'Nunito Sans';
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0em;
    text-align: left;
  }
}
.rf-button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  height: 56px;
  border-radius: 8px;
  @media screen and (max-width: 576px) {
    width: 100%;
  }
  &.removeVerticalPadding {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
  &.medium {
    font-size: 16px;
    padding: 16px 32px;
  }
  &:focus {
    box-shadow: 0 0 0 0.2rem #26b67f52 !important;
  }
  &.solid {
    background: $wealthy-green-500;
    border: 2px solid $wealthy-green-500;
    color: $white;
    &:not(.disabled) {
      &:active,
      &:hover {
        background: $wealthy-green-600 !important;
        color: $white !important;
        border-color: $wealthy-green-600 !important;
      }
    }
    &.disabled {
      background: $neutral-gray-100;
      color: $disabled-grey;
      border: 2px solid $neutral-gray-100;
      &:focus {
        box-shadow: none !important;
      }
    }
  }
}
.page-loader {
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
